import { Injectable } from '@angular/core';
import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TelegramErrorReporterService } from '../core/services/telegram-error-reporter.service';

/**
 * Reports failed backend calls to the Telegram alert channel.
 *
 * Report-only: the error is rethrown untouched, so every caller and the 401/CSRF
 * handling in HttpInterceptorService behave exactly as before.
 */
@Injectable()
export class TelegramErrorInterceptor implements HttpInterceptor {

  constructor(private telegramReporter: TelegramErrorReporterService) {}

  /**
   * Passes the request through and reports a backend failure before rethrowing it.
   * @param {HttpRequest<unknown>} request the outgoing request
   * @param {HttpHandler} next the next handler in the chain
   * @returns {Observable<HttpEvent<unknown>>} the unchanged response stream
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        // Only backend calls are alerted — IPFS gateways, RPC nodes and ipinfo fail
        // often enough that they would drown the channel.
        if (error instanceof HttpErrorResponse && this.isBackendRequest(request.url)) {
          this.telegramReporter.report({
            type: 'HTTP',
            message: this.describe(error),
            name: error.name,
            url: request.urlWithParams,
            method: request.method,
            status: error.status
          });
        }

        return throwError(() => error);
      })
    );
  }

  // Backend errors carry the useful text in the JSON body; transport failures only in error.message.
  private describe(error: HttpErrorResponse): string {
    const body: unknown = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    const bodyMessage = body && typeof body === 'object' ? (body as Record<string, unknown>)['message'] : undefined;
    if (typeof bodyMessage === 'string' && bodyMessage.trim()) {
      return bodyMessage;
    }

    return error.message || `HTTP ${error.status}`;
  }

  // Same base-URL membership check as HttpInterceptorService.isBackendRequest.
  private isBackendRequest(url: string): boolean {
    return [
      environment.API_BASE_URL,
      environment.API_BASE_URL_V2,
      environment.API_BASE_URL_V3
    ].some((baseUrl) => Boolean(baseUrl) && url.startsWith(baseUrl));
  }
}
